import React, { FC, useEffect, useMemo } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import {
  adminActions,
  adminActivePharmacyList,
  adminAssignedPharmacies,
  adminResetValue,
  getActivePharmacyList,
  getAssignedPharmacies,
  IAdminPharmacyListBaseData,
  successSelector,
} from '@pharmaplan/common';

import deleteIcon from '../../../../assets/svg/deleteIcon.svg';
import switchIcon from '../../../../assets/svg/switchIcon.svg';
import usePharmacyOwnership from '../../../../hooks/Admin/PharmacyList/usePharmacyOwnership';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import strings from '../../../../localization';

import styles from './styles';
import { setFooterShow } from '../../../../actions/drawerActions';

interface IAssignedAccountsPanel {
  adminId: string;
  adminName: string;
}

const AssignedAccountsPanel: FC<IAssignedAccountsPanel> = ({
  adminId,
  adminName,
}) => {
  const dispatch = useAppDispatch();

  const assignedPharmacies = useAppSelector(adminAssignedPharmacies);
  const allPharmacies = useAppSelector(adminActivePharmacyList);
  const loadSuccess = useAppSelector((state) =>
    successSelector([adminActions.getAssignedPharmacies], state));

  const { showAssignOwner, showRemoveOwner, ownershipSuccess } = usePharmacyOwnership();

  const { data } = allPharmacies ?? {};

  // The endpoint returns ids only, so names and emails are resolved here.
  const assignedAccounts = useMemo(
    () =>
      data.filter((item: IAdminPharmacyListBaseData) =>
        assignedPharmacies.includes(item.userId)),
    [data, assignedPharmacies],
  );

  const isEmpty = loadSuccess && assignedPharmacies.length === 0;

  useEffect(() => {
    dispatch(setFooterShow(false));
    dispatch(getActivePharmacyList({ page: 0, itemPerPage: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (adminId) {
      dispatch(getAssignedPharmacies(adminId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  /**
   * Reassigning or removing drops the account out of this admin's list, so the
   * ids are refetched. The report behind the drawer refreshes on its own.
   */
  useEffect(() => {
    if (ownershipSuccess && adminId) {
      dispatch(getAssignedPharmacies(adminId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownershipSuccess]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue(['assignedPharmacies', 'activePharmacyList']));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.adminName}>{adminName}</Typography>

      {isEmpty && (
        <Typography sx={styles.empty}>{strings.noAssignedAccounts}</Typography>
      )}

      {assignedAccounts.map((item) => {
        const { userId, pharmacyId, name, email } = item;

        return (
          <Box key={userId} sx={styles.item}>
            <Box sx={styles.details}>
              <Typography sx={styles.name}>{name}</Typography>
              <Typography sx={styles.email}>{email}</Typography>
            </Box>

            <Box sx={styles.actions}>
              <Tooltip title={strings.changeOwnership}>
                <IconButton
                  size="small"
                  onClick={showAssignOwner([pharmacyId], adminId)}
                >
                  <Box
                    component="img"
                    sx={styles.icon}
                    src={switchIcon}
                    alt={strings.changeOwnership}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title={strings.removeOwnership}>
                <IconButton
                  size="small"
                  onClick={showRemoveOwner(pharmacyId, adminId)}
                >
                  <Box
                    component="img"
                    sx={styles.icon}
                    src={deleteIcon}
                    alt={strings.removeOwnership}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssignedAccountsPanel;
